import { Injectable } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { DataService } from './data.service';
import { ApiService } from './api.service';
import { PayAtTableNiPayload } from '../models/pay-at-table.model';

/**
 * Persists fees + voucher onto the open cheque (PUT /checkout/postpaid)
 * right before a wallet (Apple Pay / Google Pay) charge.
 *
 * Card payments run this confirm from the payment pages before starting the
 * gateway session. Wallets can't await an HTTP call inside the user-gesture
 * handler that opens the payment sheet, so for them the confirm runs in the
 * post-authorization callback instead — chained before the NI session that
 * actually charges. The NI session prices the charge from the cheque's stored
 * discount, so skipping this call charges the undiscounted total.
 */
@Injectable({ providedIn: 'root' })
export class PatCheckoutConfirmService {
  constructor(
    private dataService: DataService,
    private apiService: ApiService,
  ) { }

  /** Emits true when the checkout was confirmed, false otherwise. */
  public confirm(payload: PayAtTableNiPayload, voucherName?: string): Observable<boolean> {
    const tableId = this.dataService.table()?.id;
    const restaurantId = this.dataService.restaurant()?.id;
    const userId = this.dataService.getUserId();

    if (!tableId || !restaurantId || !userId) {
      return of(false);
    }

    const confirmPayload = {
      user_id: userId,
      table_id: tableId,
      voucher_name: voucherName,
      payment_mode: payload.payment_mode,
      payment_details: payload.payment_details,
      tip_amount: payload.tip_amount,
    };

    return this.apiService.confirmPostpaidCheckout(restaurantId, confirmPayload).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }
}
